export type TransactionType = 'income' | 'expense';
export type WalletChain = 'trc20' | 'bep20' | 'erc20';
export type TokenSymbol = 'USDT' | 'TRX' | 'BNB' | 'ETH';

export interface User {
  id: string;
  name: string;
  email: string;
  password: string;
}

export interface Category {
  id: string;
  userId: string;
  name: string;
  type: TransactionType;
  color: string;
}

export interface Transaction {
  id: string;
  userId: string;
  type: TransactionType;
  /** Display amount — for wallet txs this is BDT after conversion */
  amount: number;
  categoryId: string;
  description: string;
  date: string;
  createdAt: string;
  marked?: boolean;
  /** Bank ref / on-chain tx hash — used for duplicate detection */
  reference?: string;
  /** e.g. trc20, bep20, erc20, bank */
  wallet?: string;
  walletId?: string;
  source?: 'wallet' | 'bank' | 'manual' | 'import';
  tokenSymbol?: TokenSymbol;
  /** Original on-chain amount (wallet imports only) */
  tokenAmount?: number;
  amountUsdt?: number;
}

export type SavedWallet = {
  id: string;
  label: string;
  chain: WalletChain;
  address: string;
};

export const WALLET_CHAINS: {
  id: WalletChain;
  label: string;
  tokens: string;
}[] = [
  { id: 'trc20', label: 'TRON (TRC20)', tokens: 'USDT, TRX' },
  { id: 'bep20', label: 'BSC (BEP20)', tokens: 'USDT, BNB' },
  { id: 'erc20', label: 'Ethereum (ERC20)', tokens: 'USDT, ETH' },
];

export const DEFAULT_WALLETS: SavedWallet[] = [
  {
    id: 'default-tron',
    label: 'My TRON',
    chain: 'trc20',
    address: 'TJRxzRWLP4gtsBvD8jrTE3e7vV5qLoYSU5',
  },
  {
    id: 'default-bsc',
    label: 'My BSC',
    chain: 'bep20',
    address: '0x442eDe2761B37fBB3a91D9b4E11844dfeCa832e7',
  },
];

export type CryptoRates = {
  USDT: number;
  TRX: number;
  BNB: number;
  ETH: number;
};

export const DEFAULT_USDT_PRICE_BDT = 122;

export type AppSettings = {
  currency: CurrencyCode;
  usdtPriceBdt: number;
  wallets: SavedWallet[];
};

export function convertWalletToBdt(
  tokenSymbol: TokenSymbol | string | undefined,
  tokenAmount: number | undefined,
  rates: CryptoRates,
  usdtPriceBdt: number
): { amountUsdt: number; amountBdt: number } {
  const symbol = String(tokenSymbol || 'USDT').toUpperCase() as TokenSymbol;
  const amt = Number(tokenAmount) || 0;
  const price = Number(usdtPriceBdt) || 0;
  let amountUsdt = 0;
  if (symbol === 'USDT') {
    amountUsdt = amt;
  } else {
    const tokenUsd = rates[symbol] || 0;
    const usdtUsd = rates.USDT || 1;
    amountUsdt = usdtUsd > 0 ? (amt * tokenUsd) / usdtUsd : 0;
  }
  return {
    amountUsdt: Number(amountUsdt.toFixed(6)),
    amountBdt: Number((amountUsdt * price).toFixed(2)),
  };
}

export interface TransactionFilters {
  dateFrom: string;
  dateTo: string;
  categoryId: string;
  type: TransactionType | 'all';
  /** `all` or `YYYY-MM` */
  month: string;
}

export interface FinanceTotals {
  totalIncome: number;
  totalExpense: number;
  balance: number;
  transactionCount: number;
}

export type CurrencyCode = 'BDT' | 'USD' | 'EUR' | 'GBP' | 'INR';

export const CURRENCIES: {
  code: CurrencyCode;
  name: string;
  symbol: string;
}[] = [
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
];

export const DEFAULT_CURRENCY: CurrencyCode = 'BDT';

export function getCurrencySymbol(code: CurrencyCode): string {
  return CURRENCIES.find((c) => c.code === code)?.symbol ?? '৳';
}

export const DEFAULT_USER_ID = 'local-user';

export const STORAGE_KEYS = {
  transactions: (userId: string) => `finance_transactions_${userId}`,
  categories: (userId: string) => `finance_categories_${userId}`,
  settings: (userId: string) => `finance_settings_${userId}`,
} as const;

export const DEFAULT_CATEGORIES: Omit<Category, 'id' | 'userId'>[] = [
  { name: 'Salary', type: 'income', color: '#48BB78' },
  { name: 'Freelance', type: 'income', color: '#38B2AC' },
  { name: 'Investment', type: 'income', color: '#4299E1' },
  { name: 'Other Income', type: 'income', color: '#9F7AEA' },
  { name: 'Food', type: 'expense', color: '#ED8936' },
  { name: 'Rent', type: 'expense', color: '#E53E3E' },
  { name: 'Transport', type: 'expense', color: '#DD6B20' },
  { name: 'Utilities', type: 'expense', color: '#D69E2E' },
  { name: 'Shopping', type: 'expense', color: '#ED64A6' },
  { name: 'Health', type: 'expense', color: '#38A169' },
  { name: 'Entertainment', type: 'expense', color: '#805AD5' },
  { name: 'Other Expense', type: 'expense', color: '#718096' },
];
